import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { FileText, Download, Loader2, ExternalLink, AlertCircle, Plus, Trash2, PenTool, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { formsApi, Signature } from '@/services/formsApi';
import { useNotificationStore } from '@/stores';

type FormType = 'rce' | 'ids_blank' | 'terminal_disclaimer' | 'notice_of_appeal' | 'poa_transmittal' | 'transmittal';

interface FormTypeOption {
  value: FormType;
  label: string;
  description: string;
}

const FORM_OPTIONS: FormTypeOption[] = [
  { value: 'rce', label: 'Request for Continued Examination (RCE)', description: 'SB/30 - Request for Continued Examination' },
  { value: 'ids_blank', label: 'Information Disclosure Statement (Blank)', description: 'SB/08 - Blank IDS form with application data' },
  { value: 'terminal_disclaimer', label: 'Terminal Disclaimer', description: 'Terminal Disclaimer to Obviate ODP Rejection' },
  { value: 'notice_of_appeal', label: 'Notice of Appeal', description: 'Notice of Appeal to the Patent Trial and Appeal Board' },
  { value: 'poa_transmittal', label: 'POA/ADS Transmittal', description: 'Power of Attorney / ADS Transmittal Form' },
  { value: 'transmittal', label: 'Transmittal Form', description: 'Application Transmittal Form' },
];

export function FormsPage() {
  const { addNotification } = useNotificationStore();
  const [applicationNumbers, setApplicationNumbers] = useState('');
  const [selectedFormType, setSelectedFormType] = useState<FormType>('rce');
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('');
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [defaultSignatureId, setDefaultSignatureId] = useState<string | undefined>();
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; currentApp?: string } | null>(null);
  const [errors, setErrors] = useState<{ appNo: string; error: string }[]>([]);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [showSignatureManager, setShowSignatureManager] = useState(false);
  const [newSignature, setNewSignature] = useState({ name: '', registrationNumber: '', label: '' });

  // Load signatures on mount
  useEffect(() => {
    loadSignatures();
  }, []);

  const loadSignatures = async () => {
    setIsLoadingSignatures(true);
    try {
      const result = await formsApi.getSignatures();
      setSignatures(result.signatures || []);
      setDefaultSignatureId(result.defaultSignatureId);
      if (result.defaultSignatureId) {
        setSelectedSignatureId(result.defaultSignatureId);
      }
    } catch (error) {
      console.error('Failed to load signatures:', error);
    } finally {
      setIsLoadingSignatures(false);
    }
  };

  const handleAddSignature = async () => {
    if (!newSignature.name.trim() || !newSignature.registrationNumber.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Signature',
        message: 'Please enter name, signature, and registration number.',
      });
      return;
    }

    const signature: Signature = {
      id: `sig_${Date.now()}`,
      name: newSignature.name.trim(),
      registrationNumber: newSignature.registrationNumber.trim(),
      label: newSignature.label.trim() || undefined,
    };

    const updatedSignatures = [...signatures, signature];

    setIsSavingSignature(true);
    try {
      await formsApi.updateSignatures(updatedSignatures, defaultSignatureId);
      setSignatures(updatedSignatures);
      setNewSignature({ name: '', registrationNumber: '', label: '' });
      addNotification({
        type: 'success',
        title: 'Signature Added',
        message: 'Signature has been added successfully.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error.message || 'Failed to save signature',
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleRemoveSignature = async (signatureId: string) => {
    const updatedSignatures = signatures.filter(sig => sig.id !== signatureId);
    const updatedDefaultId = defaultSignatureId === signatureId ? undefined : defaultSignatureId;

    // Update selected signature if it was removed
    if (selectedSignatureId === signatureId) {
      setSelectedSignatureId('');
    }

    setIsSavingSignature(true);
    try {
      await formsApi.updateSignatures(updatedSignatures, updatedDefaultId);
      setSignatures(updatedSignatures);
      setDefaultSignatureId(updatedDefaultId);
      addNotification({
        type: 'success',
        title: 'Signature Removed',
        message: 'Signature has been removed successfully.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Remove Failed',
        message: error.message || 'Failed to remove signature',
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleSetDefaultSignature = async (signatureId: string) => {
    setIsSavingSignature(true);
    try {
      await formsApi.updateSignatures(signatures, signatureId);
      setDefaultSignatureId(signatureId);
      addNotification({
        type: 'success',
        title: 'Default Set',
        message: 'Default signature has been updated.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message || 'Failed to set default signature',
      });
    } finally {
      setIsSavingSignature(false);
    }
  };

  // Parse application numbers from textarea
  // Split on newlines or ", " (comma followed by space) - NOT on commas without space (those are thousands separators)
  const parseApplicationNumbers = (text: string): string[] => {
    return text
      .split(/[\n]|,\s+/)  // Split on newlines or comma followed by whitespace
      .map(line => line.trim())
      .filter(line => line.length > 0)
      // Remove duplicates
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  const getSelectedSignature = (): Signature | undefined => {
    if (!selectedSignatureId || selectedSignatureId === '__none__') return undefined;
    return signatures.find(sig => sig.id === selectedSignatureId);
  };

  const handleGenerate = async () => {
    const appNumbers = parseApplicationNumbers(applicationNumbers);

    if (appNumbers.length === 0) {
      addNotification({
        type: 'error',
        title: 'No Applications',
        message: 'Please enter at least one application number.',
      });
      return;
    }

    setIsGenerating(true);
    setErrors([]);
    setProgress({ current: 0, total: appNumbers.length });

    const selectedSignature = getSelectedSignature();
    const generatedFiles: { appNo: string; fileKey: string; filename: string }[] = [];
    const failedApps: { appNo: string; error: string }[] = [];

    try {
      for (let i = 0; i < appNumbers.length; i++) {
        const appNo = appNumbers[i];
        // Clean app number for backend - remove slashes and commas
        const cleanAppNo = appNo.replace(/[\/,]/g, '');
        setProgress({ current: i + 1, total: appNumbers.length, currentApp: appNo });

        try {
          let jobId: string;

          // Submit form based on type (send cleaned app number to backend)
          switch (selectedFormType) {
            case 'rce':
              jobId = await formsApi.submitRceForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            case 'ids_blank':
              jobId = await formsApi.submitIdsForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            case 'terminal_disclaimer':
              jobId = await formsApi.submitTerminalDisclaimerForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            case 'notice_of_appeal':
              jobId = await formsApi.submitNoticeOfAppealForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            case 'poa_transmittal':
              jobId = await formsApi.submitPoaTransmittalForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            case 'transmittal':
              jobId = await formsApi.submitTransmittalForm({ appNo: cleanAppNo, signature: selectedSignature });
              break;
            default:
              throw new Error(`Unknown form type: ${selectedFormType}`);
          }

          // Wait for job completion
          const result = await formsApi.waitForJobCompletion(jobId);

          if (result.fileKey) {
            const formLabel = FORM_OPTIONS.find(opt => opt.value === selectedFormType)?.label || selectedFormType;
            const cleanAppNo = appNo.replace(/[\/,]/g, '-');
            generatedFiles.push({
              appNo,
              fileKey: result.fileKey,
              filename: `${formLabel}_${cleanAppNo}.pdf`,
            });
          }
        } catch (error: any) {
          console.error(`Failed to generate form for ${appNo}:`, error);
          failedApps.push({
            appNo,
            error: error.message || 'Unknown error',
          });
        }
      }

      // Set errors for display
      setErrors(failedApps);

      // Download files
      if (generatedFiles.length === 0) {
        addNotification({
          type: 'error',
          title: 'Generation Failed',
          message: 'No forms were successfully generated.',
        });
      } else if (generatedFiles.length === 1) {
        // Single file - download directly as PDF
        const file = generatedFiles[0];
        const blob = await formsApi.downloadFile(file.fileKey);
        downloadBlob(blob, file.filename);

        addNotification({
          type: 'success',
          title: 'Form Generated',
          message: `Successfully generated form for ${file.appNo}.`,
        });
      } else {
        // Multiple files - create zip
        await downloadAsZip(generatedFiles);

        addNotification({
          type: 'success',
          title: 'Forms Generated',
          message: `Successfully generated ${generatedFiles.length} forms.${failedApps.length > 0 ? ` ${failedApps.length} failed.` : ''}`,
        });
      }
    } catch (error: any) {
      console.error('Form generation error:', error);
      addNotification({
        type: 'error',
        title: 'Generation Failed',
        message: error.message || 'Failed to generate forms.',
      });
    } finally {
      setIsGenerating(false);
      setProgress(null);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadAsZip = async (files: { appNo: string; fileKey: string; filename: string }[]) => {
    // Dynamically import JSZip
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Download each file and add to zip
    for (const file of files) {
      const blob = await formsApi.downloadFile(file.fileKey);
      zip.file(file.filename, blob);
    }

    // Generate and download zip
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const formLabel = FORM_OPTIONS.find(opt => opt.value === selectedFormType)?.label || selectedFormType;
    const timestamp = new Date().toISOString().split('T')[0];
    downloadBlob(zipBlob, `${formLabel}_${timestamp}.zip`);
  };

  const appNumbers = parseApplicationNumbers(applicationNumbers);
  const selectedFormOption = FORM_OPTIONS.find(opt => opt.value === selectedFormType);

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">USPTO Forms Generator</h1>
          <p className="text-gray-600 mt-2">Generate pre-filled USPTO forms for multiple applications</p>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Batch Form Generation
            </CardTitle>
            <CardDescription>
              Enter application numbers and select a form type to generate forms for multiple applications at once.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Application Numbers */}
            <div className="space-y-2">
              <Label htmlFor="applicationNumbers">Application Numbers</Label>
              <Textarea
                id="applicationNumbers"
                placeholder="Enter application numbers, one per line or comma-separated:&#10;16/123,456&#10;17/234,567&#10;18/345,678"
                rows={6}
                value={applicationNumbers}
                onChange={(e) => setApplicationNumbers(e.target.value)}
                disabled={isGenerating}
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                {appNumbers.length === 0
                  ? 'Enter at least one application number'
                  : `${appNumbers.length} application${appNumbers.length === 1 ? '' : 's'} entered`
                }
              </p>
            </div>

            {/* Form Type Selection */}
            <div className="space-y-2">
              <Label htmlFor="formType">Form Type</Label>
              <Select
                value={selectedFormType}
                onValueChange={(value) => setSelectedFormType(value as FormType)}
                disabled={isGenerating}
              >
                <SelectTrigger id="formType">
                  <SelectValue placeholder="Select a form type..." />
                </SelectTrigger>
                <SelectContent>
                  {FORM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFormOption && (
                <p className="text-xs text-gray-500">{selectedFormOption.description}</p>
              )}
            </div>

            {/* Signature Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="signature">Signature (Optional)</Label>
                  <a
                    href="https://www.uspto.gov/sites/default/files/documents/sigexamples_alt_text.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    title="USPTO Signature Examples"
                  >
                    Examples <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSignatureManager(!showSignatureManager)}
                  className="text-xs"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Manage Signatures
                  {showSignatureManager ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                </Button>
              </div>
              {isLoadingSignatures ? (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading signatures...
                </div>
              ) : signatures.length > 0 ? (
                <Select
                  value={selectedSignatureId}
                  onValueChange={setSelectedSignatureId}
                  disabled={isGenerating}
                >
                  <SelectTrigger id="signature">
                    <SelectValue placeholder="No signature (leave blank)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No signature</SelectItem>
                    {signatures.map((sig) => (
                      <SelectItem key={sig.id} value={sig.id || `sig_${sig.name}`}>
                        {sig.name} #{sig.registrationNumber}
                        {sig.label && ` - ${sig.label}`}
                        {defaultSignatureId === sig.id && ' - Default'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border">
                  No signatures saved. Click "Manage Signatures" to add one.
                </div>
              )}

              {/* Signature Manager */}
              <Collapsible open={showSignatureManager} onOpenChange={setShowSignatureManager}>
                <CollapsibleContent>
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg border space-y-4">
                    {/* Add New Signature */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-gray-700">Add New Signature</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="sigName" className="text-xs">Name *</Label>
                          <Input
                            id="sigName"
                            placeholder="e.g., John Doe"
                            value={newSignature.name}
                            onChange={(e) => setNewSignature(prev => ({ ...prev, name: e.target.value }))}
                            disabled={isSavingSignature}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="sigRegNumber" className="text-xs">Registration # *</Label>
                          <Input
                            id="sigRegNumber"
                            placeholder="e.g., 12345"
                            value={newSignature.registrationNumber}
                            onChange={(e) => setNewSignature(prev => ({ ...prev, registrationNumber: e.target.value }))}
                            disabled={isSavingSignature}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="sigLabel" className="text-xs">Label (Optional)</Label>
                          <Input
                            id="sigLabel"
                            placeholder="e.g., Primary Attorney"
                            value={newSignature.label}
                            onChange={(e) => setNewSignature(prev => ({ ...prev, label: e.target.value }))}
                            disabled={isSavingSignature}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={handleAddSignature}
                        size="sm"
                        variant="outline"
                        disabled={isSavingSignature}
                      >
                        {isSavingSignature ? (
                          <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3 mr-2" />
                        )}
                        Add Signature
                      </Button>
                    </div>

                    {/* Saved Signatures List */}
                    {signatures.length > 0 && (
                      <div className="space-y-2 pt-3 border-t">
                        <h4 className="font-medium text-sm text-gray-700">Saved Signatures</h4>
                        <div className="space-y-2">
                          {signatures.map((sig) => (
                            <div key={sig.id} className="flex items-center justify-between p-2 bg-white border rounded">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <PenTool className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <div className="truncate">
                                  <span className="font-medium text-sm">{sig.name}</span>
                                  <span className="text-xs text-gray-600 ml-1">#{sig.registrationNumber}</span>
                                  {sig.label && (
                                    <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded ml-2">
                                      {sig.label}
                                    </span>
                                  )}
                                  {defaultSignatureId === sig.id && (
                                    <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded ml-1">
                                      Default
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1 flex-shrink-0 ml-2">
                                {defaultSignatureId !== sig.id && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSetDefaultSignature(sig.id!)}
                                    disabled={isSavingSignature}
                                    className="text-xs h-7 px-2"
                                  >
                                    Set Default
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveSignature(sig.id!)}
                                  disabled={isSavingSignature}
                                  className="h-7 px-2"
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Progress Display */}
            {isGenerating && progress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">
                      Generating forms... ({progress.current}/{progress.total})
                    </p>
                    {progress.currentApp && (
                      <p className="text-sm text-blue-700">
                        Processing: {progress.currentApp}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Error Display */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">
                      Failed to generate {errors.length} form{errors.length === 1 ? '' : 's'}:
                    </p>
                    <ul className="mt-2 text-sm text-red-700 space-y-1">
                      {errors.map((err, idx) => (
                        <li key={idx}>
                          <span className="font-mono">{err.appNo}</span>: {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setApplicationNumbers('');
                  setErrors([]);
                }}
                disabled={isGenerating}
              >
                Clear
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || appNumbers.length === 0}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generate {appNumbers.length > 0 ? `${appNumbers.length} Form${appNumbers.length === 1 ? '' : 's'}` : 'Forms'}
                  </>
                )}
              </Button>
            </div>

            {/* Info Box */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
              <h4 className="font-medium text-gray-900 mb-2">How it works:</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Enter one or more US application numbers</li>
                <li>Select the form type you want to generate</li>
                <li>Optionally select a signature from your saved signatures</li>
                <li>Click "Generate" to create pre-filled forms</li>
                <li>Single application: downloads a PDF directly</li>
                <li>Multiple applications: downloads a ZIP file containing all PDFs</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
